import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { signatureDocumentApi } from '@/services/api'
import { useApplicationStore } from '@/config/store/applicationStore'
import { useUserStore } from '@/config/store/userStore'

type TYouSignMember = {
  id?: string
  email?: string
  status?: string
}

export type TYouSignSignatureDocument = {
  procedureId?: string | null
  status?: string | null
  isProduction?: boolean
  members?: TYouSignMember[]
}

type TYouSignSigner = {
  status?: string
  signature_link?: string | null
  /** Set by the API when the YouSign request is past its expiration date */
  signature_request_status?: 'expired'
}

type TUseYouSignSignatureOptions = {
  /** The signature document currently displayed; re-read on every access so it can change. */
  signatureDocument: () => TYouSignSignatureDocument | null | undefined
  /** Re-fetch the owning entity. Resolve true once the signature is finished. */
  refresh: () => Promise<boolean>
  onError?: (error: unknown) => void
  /** Called once the backend confirms the signature is finished. */
  onSigned?: () => void
  /** Keep the modal visible when the YouSign link cannot be resolved (mandatory gate). */
  keepOpenOnError?: boolean
}

const POLLING_DELAYS = [2000, 2000, 4000, 6000, 6000]

const isYouSignOrigin = (origin: string) => {
  try {
    const { hostname } = new URL(origin)
    return hostname === 'yousign.app' || hostname.endsWith('.yousign.app') || hostname.endsWith('.yousign.com')
  } catch {
    return false
  }
}

const youSignEventName = (payload: unknown) => {
  if (typeof payload === 'string') {
    try {
      return youSignEventName(JSON.parse(payload))
    } catch {
      return payload.toLowerCase()
    }
  }

  if (payload && typeof payload === 'object') {
    const data = payload as { event?: string, type?: string, name?: string }
    return String(data.event || data.type || data.name || '').toLowerCase()
  }

  return ''
}

const isYouSignSuccessEvent = (payload: unknown) => {
  const eventName = youSignEventName(payload)
  return eventName === 'success' || eventName === 'signer.done' || eventName === 'signature.done' || eventName === 'done'
}

/**
 * Drives the YouSign signing modal: resolve the signer link, show the signed PDF once it is done,
 * and poll the backend for a short while after the modal is closed — YouSign notifies us through a
 * webhook, so the status lands slightly after the user finishes signing.
 */
export const useYouSignSignature = ({
  signatureDocument,
  refresh,
  onError,
  onSigned,
  keepOpenOnError = false,
}: TUseYouSignSignatureOptions) => {
  const { t } = useI18n({ useScope: 'global' })
  const userStore = useUserStore()
  const applicationStore = useApplicationStore()

  const isOpen = ref(false)
  const isOpening = ref(false)
  const isRenewing = ref(false)
  const isPolling = ref(false)
  const linkURL = ref('')
  const pollingTimeout = ref<ReturnType<typeof setTimeout> | null>(null)

  const member = computed(() => {
    const emails = [userStore.user.user?.email, userStore.user.mailPersonal]
      .filter(Boolean)
      .map(email => String(email).trim())

    return signatureDocument()?.members?.find(candidate => emails.includes(String(candidate.email || '').trim()))
  })

  const isSigned = computed(() => signatureDocument()?.status === 'finished')

  /** Shown instead of the opening label while an expired request is renewed */
  const renewingLabel = computed(() => isRenewing.value ? t('components.youSignSignature.renewing') : '')

  const parseSignerResponse = (payload: unknown): TYouSignSigner => {
    if (typeof payload === 'string') return JSON.parse(payload)
    return (payload ?? {}) as TYouSignSigner
  }

  // The YouSign sandbox refuses to render inside an iframe unless domain validation is disabled.
  const withSandboxDomainBypass = (signatureLink: string) => {
    const parsed = new URL(signatureLink)
    parsed.searchParams.set('disable_domain_validation', 'true')
    return parsed.toString()
  }

  const clearPolling = () => {
    if (!pollingTimeout.value) return
    clearTimeout(pollingTimeout.value)
    pollingTimeout.value = null
  }

  const startPolling = () => {
    if (isPolling.value || isSigned.value) return

    clearPolling()
    isPolling.value = true
    let pollIndex = 0

    const poll = () => {
      const delay = POLLING_DELAYS[pollIndex]
      if (!delay) {
        isPolling.value = false
        return
      }

      pollingTimeout.value = setTimeout(async () => {
        pollingTimeout.value = null

        try {
          if (await refresh()) {
            isPolling.value = false
            onSigned?.()
            return
          }
        } catch (error) {
          console.error('Error refreshing signature:', error)
        }

        pollIndex += 1
        poll()
      }, delay)
    }

    poll()
  }

  const openModal = async () => {
    const document = signatureDocument()
    isOpening.value = true
    isOpen.value = true
    clearPolling()
    isPolling.value = false

    try {
      const signerResponse = await signatureDocumentApi.getSignatureUrl({
        procedureId: document?.procedureId as string,
        memberId: member.value?.id as string,
      }).promise

      let signer = parseSignerResponse(signerResponse)

      if (signer.signature_request_status === 'expired') {
        // Past its YouSign expiration date: say so while it is renewed, then open the renewed one
        isRenewing.value = true
        const renewed = await signatureDocumentApi.renewSignature({
          procedureId: document?.procedureId as string,
          memberId: member.value?.id as string,
        }).promise
        signer = parseSignerResponse(renewed.signer)
        applicationStore.pushError({ title: t('components.youSignSignature.renewed'), color: 'success' })
        // A recreated request has new ids, the next opening needs them
        if (renewed.procedureId !== document?.procedureId) {
          refresh().catch(error => console.error('Error refreshing renewed signature:', error))
        }
      }

      if (signer.status === 'signed') {
        // Opening already signed on YouSign: the API catches up our DB. Don't re-open the
        // YouSign iframe — refresh and move on (mandatory queue) or show the signed PDF.
        if (await refresh()) {
          onSigned?.()
          if (onSigned) {
            isOpen.value = false
            linkURL.value = ''
            return
          }
        } else {
          startPolling()
        }

        const file = await signatureDocumentApi.getFile({ id: document?.procedureId as string }).promise
        linkURL.value = `data:application/pdf;base64,${file.base64}`
      } else if (!signer.signature_link) {
        throw new Error('Missing YouSign signature link')
      } else {
        const signatureLink = document?.isProduction
          ? signer.signature_link
          : withSandboxDomainBypass(signer.signature_link)
        if (!/^https:\/\//i.test(signatureLink)) throw new Error('Invalid YouSign signature link')
        linkURL.value = signatureLink
      }
    } catch (error) {
      console.error('Error opening signature document:', error)
      onError?.(error)
      if (!keepOpenOnError) {
        isOpen.value = false
        linkURL.value = ''
      }
    } finally {
      isOpening.value = false
      isRenewing.value = false
    }
  }

  const handleYouSignMessage = async (event: MessageEvent) => {
    if (!isOpen.value || !isYouSignOrigin(event.origin) || !isYouSignSuccessEvent(event.data)) return

    try {
      if (await refresh()) {
        clearPolling()
        isPolling.value = false
        onSigned?.()
        return
      }
    } catch (error) {
      console.error('Error refreshing signature after YouSign event:', error)
    }

    startPolling()
  }

  const closeModal = (isVisible = false) => {
    if (isVisible) {
      isOpen.value = true
      return
    }

    const shouldPoll = !isSigned.value && Boolean(linkURL.value) && !isOpening.value
    isOpen.value = false
    linkURL.value = ''

    if (shouldPoll) startPolling()
  }

  onMounted(() => window.addEventListener('message', handleYouSignMessage))
  onBeforeUnmount(() => {
    window.removeEventListener('message', handleYouSignMessage)
    clearPolling()
  })

  return {
    isOpen,
    isOpening,
    isPolling,
    isSigned,
    linkURL,
    renewingLabel,
    member,
    openModal,
    closeModal,
    clearPolling,
  }
}
